import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Button, Callout, Typography, editedText } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { openInNewTabProps } from '~/constants';
import { useLinkTaxMapping } from '~/hooks/reactQuery/mutations/useLinkTaxMapping';
import { useAccountingTaxRates } from '~/hooks/reactQuery/queries/useAccountingTaxRates';
import { useSalesTaxConfig } from '~/hooks/reactQuery/queries/useSalesTaxConfig/useSalesTaxConfig';
import { useLocaleKeys } from '~/i18n';
import { IntegrationMappingModal } from '~/integrations/IntegrationMappingModal/IntegrationMappingModal';
import { helpArticles } from '~/utils/helpArticles';
import { countMappedUnits, deriveMapTaxRatesState, formatPercent, mappableUnitsOf, ratesMatch } from '~/utils/salesTax';
export const GetMapTaxRatesModalTID = (parentTestId)=>getTestIds(parentTestId);
export const MapTaxRatesModal = (param)=>{
    let { integration, dismissModal, testId } = param;
    const { t } = useLocaleKeys('integrations.accounting.mapTaxRates');
    const { MapTaxRatesModalTestIds } = useMemo(()=>GetMapTaxRatesModalTID(testId), [
        testId
    ]);
    const { config, isLoading: configLoading } = useSalesTaxConfig({
        refreshOnHookMount: true
    });
    const { taxRates, isLoading: taxRatesLoading } = useAccountingTaxRates(integration, {
        refreshOnHookMount: true
    });
    const { linkTaxMapping } = useLinkTaxMapping(integration);
    const units = useMemo(()=>mappableUnitsOf(config, integration), [
        config,
        integration
    ]);
    const unitById = useMemo(()=>new Map(units.map((unit)=>[
                unit.id,
                unit
            ])), [
        units
    ]);
    const integrationName = t.integrationName[integration]();
    const hasRates = deriveMapTaxRatesState(taxRates) === 'ratesExist';
    const mappedCount = countMappedUnits(units);
    const rows = units.map((unit)=>{
        var _unit_integratedId;
        return {
            id: unit.id,
            anchor: "".concat(unit.name, " (").concat(formatPercent(unit.rate), ")"),
            mapping: (_unit_integratedId = unit.integratedId) !== null && _unit_integratedId !== void 0 ? _unit_integratedId : '',
            status: unit.integratedId != null ? 'mapped' : 'notMapped'
        };
    });
    return /*#__PURE__*/ _jsx(IntegrationMappingModal, {
        testId: MapTaxRatesModalTestIds.Root(),
        title: t.title({
            integration: integrationName
        }),
        subheader: t.subheader({
            integration: integrationName
        }),
        state: configLoading || taxRatesLoading ? 'loading' : 'valid',
        items: rows,
        anchorHeader: t.columns.taxUnit(),
        select: {
            header: t.columns.accountingRate({
                integration: integrationName
            }),
            selectBi: {
                name: 'map_tax_rates_select'
            },
            emptySearchText: t.noOptions(),
            placeholder: t.statusNotMapped(),
            disabled: ()=>!hasRates,
            options: (row)=>taxRates.map((taxRate)=>{
                    var _ref;
                    var _unitById_get;
                    return {
                        value: taxRate.integratedId,
                        label: "".concat(taxRate.name, " (").concat(formatPercent(taxRate.rate), ")"),
                        disabled: !ratesMatch((_ref = (_unitById_get = unitById.get(row.id)) === null || _unitById_get === void 0 ? void 0 : _unitById_get.rate) !== null && _ref !== void 0 ? _ref : '', taxRate.rate),
                        disabledTooltip: t.percentageMismatch()
                    };
                })
        },
        onMap: (param)=>{
            let { row, value } = param;
            return linkTaxMapping({
                taxUnitId: row.id,
                integratedId: editedText(value) === '' ? null : editedText(value)
            });
        },
        banner: !hasRates && /*#__PURE__*/ _jsx(Callout, {
            intent: "warning",
            withAction: /*#__PURE__*/ _jsx(Button, _object_spread_props(_object_spread({
                as: "a",
                href: helpArticles.salesTaxMapping()
            }, openInNewTabProps), {
                skin: "blackOutline",
                size: "small",
                bi: {
                    name: 'map_tax_rates_learn_more'
                },
                testId: MapTaxRatesModalTestIds.LearnMore(),
                children: t.learnMore()
            })),
            children: /*#__PURE__*/ _jsx(Typography, {
                testId: MapTaxRatesModalTestIds.NoRatesMessage(),
                children: t.noRatesMessage({
                    integration: integrationName
                })
            })
        }),
        summary: t.summary({
            mapped: String(mappedCount),
            total: String(units.length)
        }),
        closeBi: {
            name: 'map_tax_rates_modal_close'
        },
        onClose: dismissModal
    });
};
